import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CircularProgress, Button } from '@material-ui/core';
import BookmarkIcon from '@material-ui/icons/Bookmark';
import PDFUpload from '../Components/PDFUpload';
import AudioUpload from '../Components/AudioUpload';
import ReservationForm from '../Components/ReservationForm';
import { AuthContext } from '../Context/AuthContext';
import './BookDetails.css';
import noBookCover from '../assets/no-book-cover.png'; // Import local placeholder image
import config from '../config';

function BookDetails() {
  const { id } = useParams();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPDF, setShowPDF] = useState(false);
  const [showAudioPlayer, setShowAudioPlayer] = useState(false);
  const [openReservationForm, setOpenReservationForm] = useState(false);
  const API_URL = config.API_URL;
  const { user } = useContext(AuthContext);

  useEffect(() => {
    const fetchBook = async () => {
      try {
        const res = await axios.get(`${API_URL}api/books/getbook/${id}`);
        setBook(res.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching book details:', err);
        setError('Failed to load book details. Please try again later.');
        setLoading(false);
      }
    };

    fetchBook();
  }, [id, API_URL]);

  const handlePDFUploadSuccess = (pdfUrl) => {
    setBook(prev => ({
      ...prev,
      pdfUrl
    }));
    setShowPDF(true);
  };

  const handleAudioUploadSuccess = (audioUrl) => {
    setBook(prev => ({
      ...prev,
      audioUrl
    }));
    setShowAudioPlayer(true);
  };

  const togglePDFView = () => {
    setShowPDF(!showPDF);
  };

  const toggleAudioPlayer = () => {
    setShowAudioPlayer(!showAudioPlayer);
  };

  const handleOpenReservationForm = () => {
    setOpenReservationForm(true);
  };

  const handleCloseReservationForm = () => {
    setOpenReservationForm(false);
  };

  const handleReservationComplete = () => {
    // Update book status to reflect the reservation
    setBook(prev => ({
      ...prev,
      bookStatus: 'Reserved'
    }));
  };

  // Check if the user is a student
  const isStudent = user && user.userType && user.userType.toLowerCase() === 'student';
  const canUploadDigitalAssets = Boolean(user && user.isAdmin);

  if (loading) {
    return (
      <div className="book-details-loading">
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <div className="book-details-error">
        <h2>Error</h2>
        <p>{error}</p>
        <Link to="/books" className="back-button">Back to Books</Link>
      </div>
    );
  }

  if (!book) {
    return (
      <div className="book-details-not-found">
        <h2>Book Not Found</h2>
        <p>The book you're looking for doesn't exist or may have been removed.</p>
        <Link to="/books" className="back-button">Back to Books</Link>
      </div>
    );
  }

  return (
    <div className="book-details-container">
      <div className="book-details-content">
        <div className="book-details-header">
          <Link to="/books" className="back-button">
            ← Back to Books
          </Link>
        </div>

        <div className="book-details-main">
          <div className="book-details-image">
            <img
              src={book.imageUrl || noBookCover}
              alt={book.bookName}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = noBookCover;
              }}
            />
            <div className="book-status-badge">
              <span className={`status ${book.bookStatus.toLowerCase()}`}>
                {book.bookStatus}
              </span>
            </div>
          </div>

          <div className="book-details-info">
            <h1>{book.bookName}</h1>
            {book.alternateTitle && <h3>Also known as: {book.alternateTitle}</h3>}

            <div className="book-author">
              <strong>Author:</strong> {book.author}
            </div>

            <div className="book-categories">
              {book.categories && book.categories.length > 0 ? (
                <>
                  <strong>Categories:</strong>
                  <div className="category-tags">
                    {book.categories.map((category, index) => (
                      <span key={index} className="category-tag">
                        {typeof category === 'object' ? category.categoryName : category}
                      </span>
                    ))}
                  </div>
                </>
              ) : (
                <span><strong>Category:</strong> Uncategorized</span>
              )}
            </div>

            {book.description && (
              <div className="book-description">
                <strong>Description:</strong>
                <p>{book.description}</p>
              </div>
            )}

            <div className="book-details-table">
              <div className="details-row">
                <div className="detail-label">Language:</div>
                <div className="detail-value">{book.language || 'Not specified'}</div>
              </div>

              <div className="details-row">
                <div className="detail-label">Publisher:</div>
                <div className="detail-value">{book.publisher || 'Not specified'}</div>
              </div>

              {book.publicationYear && (
                <div className="details-row">
                  <div className="detail-label">Publication Year:</div>
                  <div className="detail-value">{book.publicationYear}</div>
                </div>
              )}

              {book.isbn && (
                <div className="details-row">
                  <div className="detail-label">ISBN:</div>
                  <div className="detail-value">{book.isbn}</div>
                </div>
              )}

              {book.pageCount > 0 && (
                <div className="details-row">
                  <div className="detail-label">Pages:</div>
                  <div className="detail-value">{book.pageCount}</div>
                </div>
              )}

              <div className="details-row">
                <div className="detail-label">Available Copies:</div>
                <div className="detail-value">{book.bookCountAvailable}</div>
              </div>
            </div>

            {/* Reservation button for students */}
            {isStudent && book.bookStatus === 'Available' && (
              <div className="book-reservation-section">
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<BookmarkIcon />}
                  onClick={handleOpenReservationForm}
                  className="reserve-button"
                >
                  Reserve Book
                </Button>
              </div>
            )}

            <div className="book-pdf-section">
              {book.pdfUrl ? (
                <div className="pdf-viewer-container">
                  <button onClick={togglePDFView} className="view-pdf-button">
                    {showPDF ? 'Hide PDF' : 'View PDF'}
                  </button>
                  {showPDF && (
                    <div className="pdf-iframe-container">
                      <iframe
                        src={book.pdfUrl}
                        title={`${book.bookName} PDF`}
                        className="pdf-iframe"
                        allow="fullscreen"
                      ></iframe>
                    </div>
                  )}
                </div>
              ) : (
                <p>No PDF available for this book</p>
              )}

              {/* Only show PDF upload for staff with permission */}
              <PDFUpload
                bookId={id}
                onUploadSuccess={handlePDFUploadSuccess}
                canUpload={canUploadDigitalAssets}
              />
            </div>

            <div className="book-audio-section">
              <div className="audio-section-header">
                <h3>Audio Book</h3>
                {book.audioUrl && (
                  <button onClick={toggleAudioPlayer} className="audio-toggle-button">
                    {showAudioPlayer ? 'Hide Player' : 'Listen Now'}
                  </button>
                )}
              </div>

              {book.audioUrl ? (
                <div className={`audio-player-container ${showAudioPlayer ? 'visible' : ''}`}>
                  {showAudioPlayer && (
                    <audio controls src={book.audioUrl} className="audio-player">
                      Your browser does not support the audio element.
                    </audio>
                  )}
                </div>
              ) : (
                <p className="audio-empty-state">No audio version available for this book</p>
              )}

              <AudioUpload
                bookId={id}
                onUploadSuccess={handleAudioUploadSuccess}
                canUpload={canUploadDigitalAssets}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Reservation Form Dialog */}
      <ReservationForm
        bookId={id}
        bookName={book.bookName}
        open={openReservationForm}
        handleClose={handleCloseReservationForm}
        onReservationComplete={handleReservationComplete}
      />
    </div>
  );
}

export default BookDetails; 